import { useTranslation } from 'react-i18next';

import {
    Body2,
    FilledCard,
    Stack,
    Subtitle1,
    Subtitle2,
} from '@myphx-engineering/phx-design-system';
import { constants } from '@myphx-engineering/phx-design-system/framework';

import type {
    AuthorizedMedicationInfoFragment,
    MedicationInfoFragment,
    UnknownMedicationInfoFragment,
} from '../../../graphql/generated/graphql';
import { useMedicationPricing } from '../../../hooks/use-medication-pricing';
import type { NdcDefinition } from '../../../types/drug-config';
import { formatToLocalizedCurrency } from '../../../util';
import type { PricingInfo } from '../../../util/config/get-pricing';

type MedicationPricingProps = {
    pricingInfo: PricingInfo;
    medication?:
        | AuthorizedMedicationInfoFragment
        | MedicationInfoFragment
        | UnknownMedicationInfoFragment;
    ndcDefinition?: NdcDefinition;
    'data-testid'?: string;
};

const MedicationPricing = ({
    pricingInfo,
    medication,
    ndcDefinition,
    'data-testid': testId,
}: MedicationPricingProps) => {
    const { t } = useTranslation('lilly');
    const { price, savingsAmount } = useMedicationPricing(
        pricingInfo,
        medication,
        ndcDefinition
    );

    const pricing =
        pricingInfo.type === 'selfPay'
            ? {
                  option: t('pricing.cashPrice'),
                  value:
                      savingsAmount === 0
                          ? `${price}*`
                          : t('pricing.cashValue', { price }),
                  details: savingsAmount ? (
                      <Body2>
                          {t('pricing.savingsProgram', {
                              amount: formatToLocalizedCurrency(savingsAmount),
                              reg: constants.unicode.REGISTERED,
                          })}
                      </Body2>
                  ) : undefined,
              }
            : {
                  option: t('pricing.withInsurance'),
                  value: t('pricing.insuranceValue', { price }),
              };

    return (
        <FilledCard withBorder data-testid={testId}>
            <Stack gap="xxxs">
                <Subtitle2>{pricing.option}</Subtitle2>
                <Subtitle1>{pricing.value}</Subtitle1>
                {pricing.details}
            </Stack>
        </FilledCard>
    );
};

export default MedicationPricing;
